import {
    Box, Button, Grid, TextField, Typography, IconButton,
    MenuItem, Select, FormControl, InputLabel
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaPaperPlane, FaPen, FaTrash } from "react-icons/fa6";
import CustomContainer from "../../components/CustomContainer";
import PageHeader from "../../components/PageHeader";
import AppCard from "../../components/ui/AppCard";
import appToast from "../../components/appToast";
import { getCategories } from "../../helpers/httpRequests/product_props";
import { getProductDetails } from "../../helpers/httpRequests/products";
import { createQuote } from "../../helpers/httpRequests/quotes";
import { metalList } from "../../utils/variables";

const formatOptions = (data) => data?.data?.data?.map(item => ({ value: item.id, label: item.name })) ?? [];

// Reaproveita o resize/quadrado do fluxo de orçamento.
const readImageAsSquare = (file) => new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const size = 500;
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext('2d');
            const aspectRatio = img.width / img.height;
            let drawWidth, drawHeight, offsetX, offsetY;
            if (aspectRatio > 1) {
                drawWidth = size * aspectRatio;
                drawHeight = size;
                offsetX = -(drawWidth - size) / 2;
                offsetY = 0;
            } else {
                drawWidth = size;
                drawHeight = size / aspectRatio;
                offsetX = 0;
                offsetY = -(drawHeight - size) / 2;
            }
            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
            resolve(canvas.toDataURL('image/jpeg'));
        };
        img.src = event.target.result;
    };
    reader.readAsDataURL(file);
});

export default function CustomOrder() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [images, setImages] = useState([]);
    const [form, setForm] = useState({ name: '', category: '', metal: '', description: '' });
    const [submitting, setSubmitting] = useState(false);

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 60 });
    const categories = formatOptions(categoriesQuery.data);

    const productQuery = useQuery({
        queryKey: ['getProductDetails', id],
        queryFn: () => getProductDetails(id),
        enabled: !!id,
        staleTime: 1000 * 60,
    });
    const baseProduct = productQuery.data?.data?.data;

    useEffect(() => {
        if (baseProduct) {
            setForm((f) => ({
                ...f,
                name: f.name || `Customização de ${baseProduct.name}`,
                category: f.category || baseProduct.category || '',
            }));
        }
    }, [baseProduct]);

    const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

    const handleImageUpload = async (e) => {
        if (images.length >= 3) return;
        const file = e.target.files[0];
        if (!file) return;
        const base64 = await readImageAsSquare(file);
        setImages((prev) => [...prev, base64]);
        e.target.value = '';
    };

    const handleRemoveImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (images.length < 1) {
            appToast.error('Envie ao menos uma imagem de referência da joia.');
            return;
        }
        setSubmitting(true);
        try {
            await createQuote({
                name: form.name,
                category_id: form.category,
                selected_metal: form.metal,
                description: form.description,
                images,
            });
            navigate('/orcamento-resultado/success');
        } catch (error) {
            appToast.error(error.message || 'Não foi possível enviar sua solicitação.');
            navigate('/orcamento-resultado/error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <CustomContainer>
            <Box component="form" onSubmit={handleSubmit} sx={{ my: '2rem' }}>
                <PageHeader
                    title="Personalizar joia"
                    crumbs={[{ label: "Meus orçamentos", to: "/orcamentos" }, "Personalização"]}
                />

                <Typography sx={{ color: 'text.secondary', mb: '1.5rem', maxWidth: 640 }}>
                    Descreva a joia que você quer criar ou reformar e envie imagens de referência.
                    Nossa equipe analisa, ajusta e responde com um orçamento pelo seu histórico de orçamentos.
                    {baseProduct ? ` Partindo do modelo ${baseProduct.name}.` : ''}
                </Typography>

                <AppCard>
                    <Typography sx={styles.eyebrow}>Referências</Typography>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {images.map((src, index) => (
                            <Grid key={index} size={{ xs: 6, sm: 3, md: 2 }} sx={styles.imageItem}>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveImage(index)}
                                    sx={styles.deleteButton}
                                    aria-label="Remover imagem"
                                >
                                    <FaTrash size={14} />
                                </IconButton>
                                <Box component="img" src={src} alt={`Referência ${index + 1}`} sx={styles.imagePreview} />
                            </Grid>
                        ))}
                        {images.length < 3 && (
                            <Grid size={{ xs: 6, sm: 3, md: 2 }} sx={styles.addImageItem}>
                                <Button variant="outlined" color="primary" component="label" startIcon={<FaPen size={14} />} fullWidth>
                                    Incluir
                                    <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                                </Button>
                            </Grid>
                        )}
                    </Grid>

                    <Typography sx={styles.eyebrow}>Informações da joia</Typography>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField required fullWidth label="Título" value={form.name} onChange={handleChange('name')} />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormControl required fullWidth>
                                <InputLabel id="cat-label">Categoria</InputLabel>
                                <Select labelId="cat-label" label="Categoria" value={form.category} onChange={handleChange('category')}>
                                    {categories.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <FormControl required fullWidth>
                                <InputLabel id="metal-label">Material</InputLabel>
                                <Select labelId="metal-label" label="Material" value={form.metal} onChange={handleChange('metal')}>
                                    {metalList.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={12}>
                            <TextField
                                required
                                multiline
                                rows={6}
                                fullWidth
                                label="Descrição da customização"
                                placeholder="Conte os detalhes: medidas, gravação, pedras, acabamento, prazo desejado."
                                value={form.description}
                                onChange={handleChange('description')}
                            />
                        </Grid>
                    </Grid>
                </AppCard>

                <Box sx={styles.submitContainer}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        startIcon={<FaPaperPlane />}
                        disabled={submitting}
                    >
                        {submitting ? 'Enviando...' : 'Enviar para análise'}
                    </Button>
                </Box>
            </Box>
        </CustomContainer>
    );
}

const styles = {
    card: {
        p: { xs: '1.25rem', md: '1.75rem' },
    },
    eyebrow: {
        fontSize: '.72rem',
        letterSpacing: '.1em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: 'secondary.dark',
        mb: '1rem',
    },
    imageItem: {
        position: 'relative',
    },
    addImageItem: {
        display: 'flex',
        alignItems: 'center',
    },
    deleteButton: {
        position: 'absolute',
        top: 12,
        right: 4,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'background.paper' },
    },
    imagePreview: {
        width: '100%',
        aspectRatio: '1 / 1',
        objectFit: 'cover',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
    },
    submitContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
        mt: '1.5rem',
    },
};
